/**
 * DutyLogCreateModal — hosts the "Create New Duty Log" modal for DLE.
 *
 * Listens for `dle:create-request` so any toolbar button in the explorer can
 * open the modal without prop drilling. Includes preview flow (enhancement
 * engine renders crosslink-enriched text before saving).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createDutyLogConfig } from './DutyLogConfig';
import EnhancedLogDisplay from './EnhancedLogDisplay';
import { DutyLogEnhancer } from './EnhancementEngine';
import AppModal from '../../common/AppModal';
import { HttpDutyLogApiClient, DutyContextResponse } from '../../../services/api/dutyLogApiClient';
import './DutyLogForm.scss';

interface DutyLogCreateModalProps {
    onCreated?: () => void;
}

export const DutyLogCreateModal: React.FC<DutyLogCreateModalProps> = ({ onCreated }) => {
    const config = useMemo(() => {
        return createDutyLogConfig({ userContext: '', userClass: '', userName: '' });
    }, []);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        const handler = () => setShowCreateModal(true);
        window.addEventListener('dle:create-request', handler);
        return () => window.removeEventListener('dle:create-request', handler);
    }, []);

    const [formData, setFormData] = useState({
        body: '',
        logUser: '',
        logClass: '',
        logContext: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({ body: '', logUser: '', logClass: '', logContext: '' });
    const [enhancedResult, setEnhancedResult] = useState<any>(null);

    // Duty context (allowed regions + computed RDO/TDO/ADMIN role) is the
    // source of truth — userlogs derives it from the JWT and re-validates
    // on write. We just render whatever it returns.
    const [dutyContext, setDutyContext] = useState<DutyContextResponse | null>(null);
    const [contextError, setContextError] = useState<string | null>(null);
    const apiClient = useMemo(() => new HttpDutyLogApiClient(), []);

    useEffect(() => {
        if (!showCreateModal) return;
        setContextError(null);
        apiClient.getDutyContext()
            .then((ctx) => {
                setDutyContext(ctx);
                setFormData({
                    body: '',
                    logUser: ctx.userName,
                    logClass: ctx.allowedRegions[0] ?? '',
                    logContext: ctx.logContext,
                });
            })
            .catch((err) => {
                console.error('[DLE] Failed to load duty context:', err);
                setContextError(err?.message ?? 'Could not load duty context');
            });
    }, [showCreateModal, apiClient]);

    const validateForm = () => {
        const errs: any = {};
        if (!formData.body.trim()) errs.body = 'Log entry content is required';
        if (!formData.logClass) errs.logClass = 'Region is required';
        if (!dutyContext?.allowedRegions.includes(formData.logClass)) {
            errs.logClass = `You do not have permission to write under ${formData.logClass}`;
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handlePreview = () => {
        if (!validateForm()) return;
        const enhancer = new DutyLogEnhancer();
        const currentYear = new Date().getFullYear();
        const result = enhancer.enhanceLogEntryWithCrosslinking(formData.body, currentYear);
        setEnhancedResult(result);
        setShowPreview(true);
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        setContextError(null);
        try {
            const enhancer = new DutyLogEnhancer();
            const currentYear = new Date().getFullYear();
            const fullResult = enhancer.enhanceLogEntryWithCrosslinking(formData.body, currentYear);
            const createRequest = {
                body: formData.body.trim(),
                logUser: formData.logUser.trim(),
                logClass: formData.logClass,
                logContext: formData.logContext,
                enhanced_body: fullResult.enhanced_body,
                enhancement_metadata: fullResult.enhancement_metadata
            };
            // @ts-ignore: config.serviceAdapter is typed for fetchData, but we want createEntity
            if (config.serviceAdapter.createEntity) {
                // @ts-ignore
                await config.serviceAdapter.createEntity(createRequest);
            }
            setShowCreateModal(false);
            setShowPreview(false);
            setFormData({ body: '', logUser: '', logClass: '', logContext: '' });
            onCreated?.();
        } catch (err: any) {
            console.error('[DLE] Create failed:', err);
            setContextError(err?.message ?? 'Failed to create duty log');
            setShowPreview(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <AppModal
                isOpen={showCreateModal && !showPreview}
                onClose={() => setShowCreateModal(false)}
                width={600}
                maxHeight="90vh"
                header="Create New Duty Log Entry (Enhanced)"
                persistent
            >
                <div className="duty-log-form-modal">
                    {contextError && (
                        <div className="form-error" style={{ marginBottom: '12px' }}>
                            {contextError}
                        </div>
                    )}
                    <form className="duty-log-form">
                        <div className="form-group">
                            <label>User Name *</label>
                            <input type="text" value={formData.logUser} disabled />
                        </div>
                        <div className="form-group">
                            <label>Region *</label>
                            {(() => {
                                const regions = dutyContext?.allowedRegions ?? [];
                                if (regions.length <= 1) {
                                    return <input type="text" value={formData.logClass} disabled />;
                                }
                                return (
                                    <select
                                        value={formData.logClass}
                                        onChange={e => setFormData(f => ({ ...f, logClass: e.target.value }))}
                                        disabled={isSubmitting}
                                    >
                                        {regions.map(r => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                );
                            })()}
                            {errors.logClass && <span className="form-error">{errors.logClass}</span>}
                        </div>
                        <div className="form-group">
                            <label>Context *</label>
                            <input type="text" value={formData.logContext} disabled />
                        </div>
                        <div style={{ margin: '0 0 12px 0', padding: '8px 12px', fontSize: '13px', color: 'var(--em3-color-text-secondary)', background: 'var(--em3-color-background-panel)', borderRadius: '4px', lineHeight: 1.5, textAlign: 'left' }}>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                <li>Duty Logs cannot be deleted.</li>
                                <li>Duty Logs cannot be edited.</li>
                                <li>Duty logs are not public information.</li>
                                <li>Use the Preview function to make sure you log says what you mean it to.</li>
                            </ul>
                        </div>
                        <div className="form-group full-width">
                            <label>Log Entry *</label>
                            <textarea value={formData.body} onChange={e => setFormData(f => ({ ...f, body: e.target.value }))} rows={6} disabled={isSubmitting} />
                            {errors.body && <span className="form-error">{errors.body}</span>}
                        </div>
                        <div className="form-actions">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)} disabled={isSubmitting}>Cancel</button>
                            <button type="button" className="btn btn-primary" onClick={handlePreview} disabled={isSubmitting}>Preview</button>
                        </div>
                    </form>
                </div>
            </AppModal>
            <AppModal
                isOpen={showCreateModal && showPreview && !!enhancedResult}
                onClose={() => setShowPreview(false)}
                width={600}
                maxHeight="90vh"
                header="Preview Duty Log Entry"
            >
                <div className="duty-log-form-modal">
                    <div className="preview-body">
                        {enhancedResult && (
                            <EnhancedLogDisplay
                                originalText={formData.body}
                                enhancedText={enhancedResult.enhanced_body}
                                metadata={enhancedResult.enhancement_metadata}
                                displayMode="enhanced"
                            />
                        )}
                        <div className="preview-meta">
                            <div><strong>User:</strong> {formData.logUser}</div>
                            <div><strong>Classification:</strong> {formData.logClass}</div>
                            <div><strong>Context:</strong> {formData.logContext}</div>
                        </div>
                        <div className="form-actions" style={{ marginTop: '16px' }}>
                            <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(false)} disabled={isSubmitting}>Edit</button>
                            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSubmitting}>{isSubmitting ? 'Creating...' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            </AppModal>
        </>
    );
};

export default DutyLogCreateModal;
